import apiClient from '@/shared/api/client'

const BASE = '/waste'

export interface WasteCategory {
  id: string
  plant_id: string
  code: string
  name: string
  source_dept: string
  waste_type: string
  is_reusable: boolean
  default_disposition: string
  reentry_stage?: string
  reusability_pct?: string | number
  max_addback_pct: string | number
  effective_yield_pct?: string | number
  secondary_waste_pct?: string | number
  secondary_disposal_rate?: string | number
  reprocessing_cost_per_kg?: string | number
  standard_sale_rate?: string | number
  hsn_code?: string
  gst_rate_pct: string | number
  disposal_cost_per_kg?: string | number
  is_active: boolean
  notes?: string
}

export interface WasteStock {
  id: string
  plant_id: string
  category_id: string
  category_code?: string
  category_name?: string
  current_balance_kg: string | number
  locked_kg: string | number
  avg_landed_cost_per_kg?: string | number
  total_value?: string | number
  first_in_date?: string
  last_in_date?: string
  updated_at: string
}

export interface WasteMovement {
  id: string
  category_id: string
  category_code?: string
  category_name?: string
  movement_type: string
  qty_kg: string | number
  balance_after_kg: string | number
  unit_cost_per_kg?: string | number
  unit_value_per_kg?: string | number
  total_value?: string | number
  source_dept?: string
  remarks?: string
  created_at: string
}

export interface StageCost {
  id: string
  plant_id: string
  stage: string
  power_cost_per_kg: string | number
  labor_cost_per_kg: string | number
  overhead_per_kg: string | number
  total_cost_per_kg: string | number
  effective_from: string
  is_active: boolean
  notes?: string
}

export interface ReuseEconomics {
  qty_reused_kg: string | number
  yarn_produced_kg: string | number
  secondary_waste_kg: string | number
  avoided_cotton_cost: string | number
  reprocessing_cost: string | number
  secondary_loss_value: string | number
  opportunity_cost_sale: string | number
  net_economic_benefit: string | number
  benefit_per_kg: string | number
  decision: 'REUSE_GOOD' | 'REUSE_MARGINAL' | 'SELL_RECOMMENDED'
  reasoning: string[]
}

export const wasteApi = {
  listCategories: (plant_id?: string, only_active = true) =>
    apiClient.get<WasteCategory[]>(`${BASE}/categories`, {
      params: { plant_id, only_active },
    }).then(r => r.data),
  createCategory: (data: Record<string, unknown>) =>
    apiClient.post<WasteCategory>(`${BASE}/categories`, data).then(r => r.data),
  updateCategory: (id: string, data: Record<string, unknown>) =>
    apiClient.patch<WasteCategory>(`${BASE}/categories/${id}`, data).then(r => r.data),

  listStock: (plant_id: string) =>
    apiClient.get<WasteStock[]>(`${BASE}/stock`, { params: { plant_id } }).then(r => r.data),
  adjust: (data: { plant_id: string; category_id: string; new_balance_kg: number; reason: string }) =>
    apiClient.post(`${BASE}/stock/adjust`, data).then(r => r.data),

  listMovements: (plant_id: string, params?: Record<string, unknown>) =>
    apiClient.get<WasteMovement[]>(`${BASE}/movements`, { params: { plant_id, ...params } }).then(r => r.data),
  manualIn: (data: Record<string, unknown>) => apiClient.post(`${BASE}/movements/manual-in`, data).then(r => r.data),

  listStageCosts: (plant_id: string) =>
    apiClient.get<StageCost[]>(`${BASE}/stage-costs`, { params: { plant_id } }).then(r => r.data),
  updateStageCost: (id: string, data: Record<string, unknown>) =>
    apiClient.patch<StageCost>(`${BASE}/stage-costs/${id}`, data).then(r => r.data),

  reuseEconomics: (data: { plant_id: string; category_id: string; qty_kg: number; fresh_cotton_landed_per_kg?: number }) =>
    apiClient.post<ReuseEconomics>(`${BASE}/reuse-economics`, data).then(r => r.data),
}
